import { useCallback, useMemo, useRef, useState } from 'react';
import type { Dispatch, SetStateAction } from 'react';

import type { StarterDashboardV2PageData } from '#src/app/pages/starterDashboardV2/pageContext';
import { useStarterDashboardV2PageData } from '#src/app/pages/starterDashboardV2/pageContext';
import { usePetitionAiGuidanceExperiment } from '#src/app/pages/starterDashboardV2/shared/hooks';
import { useSessionStorageValue } from '#src/app/shared/hooks/storage';

import { TASKS_CONFIGS } from '../../../../constants';
import { PERSONALIZED_TASK_STATUSES, PERSONALIZED_TASKS_GENERATION_STATUSES } from '../../constants';
import type { UseDynamicTasksReturn } from '../../types';
import { useDynamicCheckboxAction } from '../useDynamicCheckboxAction';
import { useDynamicTaskAction } from '../useDynamicTaskAction';
import { usePollPersonalizedTasks } from '../usePollPersonalizedTasks';

import { DEFAULT_TASKS_DISPLAY_COUNT, DYNAMIC_TASKS_EXPANDED_STORAGE_KEY, getToggleTaskListData } from './model';
import {
	buildTaskShapes,
	getDisplayedTasks,
	getTaskStats,
	orderDynamicTasksForDisplay,
	parsePersonalizedTasks,
} from './taskShapes';
import { type PendingCtaSync, usePendingPersonalizedTaskSync } from './usePendingPersonalizedTaskSync';
import { useTrackAiGuidanceGenerationStatus } from './useTrackAiGuidanceGenerationStatus';

function shouldPollPersonalizedTasks({
	generationStatus,
	isEnabled,
}: {
	generationStatus: string;
	isEnabled: boolean;
}): boolean {
	return (
		isEnabled &&
		(generationStatus === PERSONALIZED_TASKS_GENERATION_STATUSES.PENDING ||
			generationStatus === PERSONALIZED_TASKS_GENERATION_STATUSES.IN_PROGRESS)
	);
}

function useResolvedPersonalizedTasks({
	petition,
	isEnabled,
}: {
	petition: StarterDashboardV2PageData['petition'];
	isEnabled: boolean;
}) {
	const initialResult = parsePersonalizedTasks(petition.personalizedTasks);
	const polledData = usePollPersonalizedTasks({
		slug: petition.slug,
		shouldPoll: shouldPollPersonalizedTasks({
			generationStatus: initialResult.generationStatus,
			isEnabled,
		}),
	});

	return useMemo(
		() => (polledData ? parsePersonalizedTasks(polledData) : parsePersonalizedTasks(petition.personalizedTasks)),
		[polledData, petition.personalizedTasks],
	);
}

function useBuiltDynamicTasks({
	tasks,
	optimisticCompletedTaskIds,
	setOptimisticCompletedTaskIds,
	setPendingCtaSync,
	refreshData,
}: {
	tasks: ReturnType<typeof useResolvedPersonalizedTasks>['tasks'];
	optimisticCompletedTaskIds: ReadonlySet<string>;
	setOptimisticCompletedTaskIds: Dispatch<SetStateAction<ReadonlySet<string>>>;
	setPendingCtaSync: (pendingSync: PendingCtaSync) => void;
	refreshData: () => void;
}) {
	const getCtaAction = useDynamicTaskAction();
	const getCheckboxAction = useDynamicCheckboxAction();

	return useMemo(() => {
		const baseShapes = buildTaskShapes(tasks);
		const mapped = baseShapes.map((task) => {
			const uiCompleted =
				task.status === PERSONALIZED_TASK_STATUSES.COMPLETED || optimisticCompletedTaskIds.has(task.id);
			const rawCta = getCtaAction(task.featureKey, task.id, task.status);
			const runCheckboxAction = getCheckboxAction(task.featureKey, task.id, task.title);
			return {
				...task,
				uiCompleted,
				onCtaClick: rawCta
					? async () => {
							const completed = await rawCta();
							const taskFeatureKey = task.featureKey;
							if (taskFeatureKey && Object.hasOwn(TASKS_CONFIGS, taskFeatureKey)) {
								setPendingCtaSync({
									personalizedTaskId: task.id,
									featureKey: taskFeatureKey,
								});
							}
							refreshData();
							return completed;
						}
					: undefined,
				onCheckboxChange: async () => {
					setOptimisticCompletedTaskIds((previousIds) => {
						if (previousIds.has(task.id)) {
							return previousIds;
						}
						return new Set([...previousIds, task.id]);
					});
					try {
						await runCheckboxAction();
					} catch {
						setOptimisticCompletedTaskIds((previousIds) => {
							if (!previousIds.has(task.id)) {
								return previousIds;
							}
							const updatedIds = new Set(previousIds);
							updatedIds.delete(task.id);
							return updatedIds;
						});
					}
				},
			};
		});

		return orderDynamicTasksForDisplay(mapped);
	}, [
		tasks,
		optimisticCompletedTaskIds,
		setOptimisticCompletedTaskIds,
		getCtaAction,
		getCheckboxAction,
		setPendingCtaSync,
		refreshData,
	]);
}

/**
 * Hook that provides dynamic (AI-generated) tasks data.
 */
export function useDynamicTasks(): UseDynamicTasksReturn {
	const {
		data: { isEnabled: isPetitionAiGuidanceEnabled },
	} = usePetitionAiGuidanceExperiment();
	const {
		data,
		actions: { refreshData },
	} = useStarterDashboardV2PageData();
	const pendingCtaSyncRef = useRef<PendingCtaSync | null>(null);
	const clearPendingCtaSync = useCallback(() => {
		pendingCtaSyncRef.current = null;
	}, []);
	const setPendingCtaSync = useCallback((pendingSync: PendingCtaSync) => {
		pendingCtaSyncRef.current = pendingSync;
	}, []);
	usePendingPersonalizedTaskSync(data.petition, pendingCtaSyncRef, clearPendingCtaSync, refreshData);

	const parsedTasks = useResolvedPersonalizedTasks({
		petition: data.petition,
		isEnabled: isPetitionAiGuidanceEnabled,
	});

	const { generationStatus, tasks, failureContext } = parsedTasks;
	const shouldShowFallbackNextSteps = generationStatus === PERSONALIZED_TASKS_GENERATION_STATUSES.FAILED;

	const [optimisticCompletedTaskIds, setOptimisticCompletedTaskIds] = useState<ReadonlySet<string>>(() => new Set());

	useTrackAiGuidanceGenerationStatus({
		generationStatus,
		failureContext,
		isEnabled: isPetitionAiGuidanceEnabled,
		slug: data.petition.slug,
	});

	const allTasks = useBuiltDynamicTasks({
		tasks,
		optimisticCompletedTaskIds,
		setOptimisticCompletedTaskIds,
		setPendingCtaSync,
		refreshData,
	});

	const [expandedStorageValue, setExpandedStorageValue] = useSessionStorageValue(DYNAMIC_TASKS_EXPANDED_STORAGE_KEY);
	const taskListIsExpanded = expandedStorageValue === 'true';
	const displayCount = taskListIsExpanded ? allTasks.length : DEFAULT_TASKS_DISPLAY_COUNT;

	const { nextExpandedStorageValue, ...toggleData } = getToggleTaskListData(
		allTasks,
		taskListIsExpanded,
		generationStatus,
	);
	const displayedTasks = getDisplayedTasks(allTasks, displayCount);
	const stats = getTaskStats(allTasks);

	return {
		data: {
			tasks: displayedTasks,
			stats,
			generationStatus,
			shouldShowFallbackNextSteps,
			taskListIsExpanded,
			...toggleData,
		},
		actions: {
			toggleTaskList: () => setExpandedStorageValue(nextExpandedStorageValue),
		},
	};
}
